// ABOUTME: Left panel of the news article builder modal.
// ABOUTME: Clean article-like layout with inline title and dynamic text/image content sections.

import { Box, InputBase } from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import TextFieldsIcon from "@mui/icons-material/TextFields";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import ArticleTextSection from "../components/ArticleTextSection";
import ArticleImageSection from "../components/ArticleImageSection";
import SortableArticleSection from "../components/SortableArticleSection";

const ArticleBuilderView = ({
    title,
    setTitle,
    sections,
    setSections,
    colors,
    theme,
}) => {
    const { t } = useTranslation();

    const addSection = useCallback((type) => {
        const newSection = {
            id: Date.now(),
            type,
            content: null,
        };
        setSections((prev) => [...prev, newSection]);
    }, [setSections]);

    const updateSection = useCallback((id, content) => {
        setSections((prev) =>
            prev.map((s) => (s.id === id ? { ...s, content } : s))
        );
    }, [setSections]);

    const removeSection = useCallback((id) => {
        setSections((prev) => {
            const section = prev.find((s) => s.id === id);
            if (section?.type === "image" && section.content?.previewUrl) {
                URL.revokeObjectURL(section.content.previewUrl);
            }
            return prev.filter((s) => s.id !== id);
        });
    }, [setSections]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (!over || String(active.id) === String(over.id)) return;
        setSections((prev) => {
            const oldIndex = prev.findIndex((s) => String(s.id) === String(active.id));
            const newIndex = prev.findIndex((s) => String(s.id) === String(over.id));
            if (oldIndex === -1 || newIndex === -1) return prev;
            return arrayMove(prev, oldIndex, newIndex);
        });
    }, [setSections]);

    return (
        <Box
            sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
                p: 3,
            }}
        >
            {/* Title - inline editable, bold, large, wraps to next line */}
            <InputBase
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                multiline
                minRows={1}
                placeholder={t("article_title_placeholder")}
                sx={{
                    width: "100%",
                    mb: 2,
                    fontSize: 21,
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 1000,
                    color: colors.txt["primary"],
                    "& .MuiInputBase-input": {
                        padding: 0,
                    },
                    "& .MuiInputBase-input::placeholder": {
                        color: colors.txt["secondary"],
                        opacity: 0.6,
                    },
                }}
            />

            {/* Content sections */}
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={sections.map((s) => String(s.id))}
                    strategy={verticalListSortingStrategy}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                        {sections.map((section) => (
                            <SortableArticleSection
                                key={section.id}
                                id={section.id}
                                colors={colors}
                            >
                                {section.type === "text" ? (
                                    <ArticleTextSection
                                        content={section.content}
                                        onChange={(content) => updateSection(section.id, content)}
                                        onRemove={() => removeSection(section.id)}
                                        colors={colors}
                                        theme={theme}
                                    />
                                ) : (
                                    <ArticleImageSection
                                        image={section.content}
                                        onChange={(content) => updateSection(section.id, content)}
                                        onRemove={() => removeSection(section.id)}
                                        colors={colors}
                                        theme={theme}
                                    />
                                )}
                            </SortableArticleSection>
                        ))}
                    </Box>
                </SortableContext>
            </DndContext>

            {/* Add section buttons */}
            <Box
                sx={{
                    display: "flex",
                    gap: 1.5,
                    pt: 3,
                    borderTop: sections.length > 0 ? `1px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}` : "none",
                }}
            >
                <Box
                    onClick={() => addSection("text")}
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        py: 2,
                        borderRadius: 4,
                        cursor: "pointer",
                        transition: "150ms",
                        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                        color: colors.txt["secondary"],
                        "&:hover": {
                            background: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                            color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                        },
                    }}
                >
                    <TextFieldsIcon sx={{ fontSize: 22 }} />
                    <Box sx={{ fontSize: 14, fontFamily: "Roboto, sans-serif", fontWeight: 500 }}>
                        {t("add_text_section") || "Tekst toevoegen"}
                    </Box>
                </Box>
                <Box
                    onClick={() => addSection("image")}
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        py: 2,
                        borderRadius: 4,
                        cursor: "pointer",
                        transition: "150ms",
                        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                        color: colors.txt["secondary"],
                        "&:hover": {
                            background: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                            color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                        },
                    }}
                >
                    <ImageOutlinedIcon sx={{ fontSize: 22 }} />
                    <Box sx={{ fontSize: 14, fontFamily: "Roboto, sans-serif", fontWeight: 500 }}>
                        {t("add_image_section") || "Afbeelding toevoegen"}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ArticleBuilderView;
